import React from 'react';
import {
  CG_BOOST_NON_COMPARABLE_METRICS,
  CgBoostMetricKey,
  getSnapshot,
  PLATFORM_LABELS,
  SnapshotPayload,
} from '../../../services/cgBoostApi';
import { ViewDataBoundary } from '../useViewData';
import MetricLabel from '../MetricLabel';
import { cardStyle, eyebrowLabel, metricGrid, sectionTitle } from '../styles';

const APPROX_TOOLTIP =
  'Cross-platform total is approximate — TikTok, Meta, and Google Ads define this metric differently. See the Platforms tab for per-platform definitions.';

function approxTooltip(metric: CgBoostMetricKey): string | undefined {
  return CG_BOOST_NON_COMPARABLE_METRICS.includes(metric) ? APPROX_TOOLTIP : undefined;
}

interface Props { campaignId: string; }

const SnapshotView: React.FC<Props> = ({ campaignId }) => (
  <ViewDataBoundary
    loader={getSnapshot}
    campaignId={campaignId}
    loadingLabel="Loading snapshot…"
    render={(data) => <SnapshotBody data={data} />}
  />
);

const SnapshotBody: React.FC<{ data: SnapshotPayload }> = ({ data }) => {
  const { totals, productionSummary, budgetDeployment } = data;
  const deployedPct = (budgetDeployment.pctDeployed * 100).toFixed(1);

  return (
    <>
      <div style={cardStyle}>
        <div style={sectionTitle}>What did this campaign deliver overall?</div>
        <div style={metricGrid}>
          <MetricLabel metric="impressions" value={totals.impressions} tooltip={approxTooltip('impressions')} />
          <MetricLabel metric="views" value={totals.views} tooltip={approxTooltip('views')} />
          <MetricLabel metric="vtr" value={totals.vtr} tooltip={approxTooltip('vtr')} />
          <MetricLabel metric="hookRate" value={totals.hookRate} tooltip={approxTooltip('hookRate')} />
          <MetricLabel metric="cpm" value={totals.cpm} tooltip={approxTooltip('cpm')} />
          <MetricLabel metric="cpv" value={totals.cpv} tooltip={approxTooltip('cpv')} />
          <MetricLabel metric="frequency" value={totals.frequency} tooltip={approxTooltip('frequency')} />
          <div>
            <MetricLabel metric="spendPacing" />
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {deployedPct}%
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
                ${Math.round(budgetDeployment.spend).toLocaleString()} of ${budgetDeployment.plannedTotal.toLocaleString()}
              </div>
              <div style={progressTrack}>
                <div style={{ ...progressFill, width: `${Math.min(100, budgetDeployment.pctDeployed * 100)}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={sectionTitle}>Production Summary</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          <Stat label="Creators activated" value={productionSummary.creatorCount} />
          <Stat label="Assets produced" value={productionSummary.assetCount} />
          <Stat
            label="Platforms"
            value={productionSummary.platforms.map((p) => PLATFORM_LABELS[p]).join(' · ')}
          />
        </div>
      </div>
    </>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={eyebrowLabel}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value}</div>
  </div>
);

const progressTrack: React.CSSProperties = {
  marginTop: 6, height: 6, borderRadius: 'var(--radius-pill)',
  background: 'rgba(0,0,0,0.06)', overflow: 'hidden',
};

const progressFill: React.CSSProperties = {
  height: '100%', background: 'var(--accent)', borderRadius: 'var(--radius-pill)',
};

export default SnapshotView;
